// Copyright (c) 2021, 37.78 Tecnologia Ltda. All rights reserved.

import React, { useCallback, useEffect } from "react";

import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { maybeUnknownState, useAuth, userStates } from "../../contexts/auth";
import { useLoading } from "../user/loadingContext";
import { getRouteGuards } from "./getRouteGuards";

export const AuthenticatedGuards = () => {
  const {
    isCustomTokenLogin,
    logout,
    userState,
    userState: {
      logged,
      currentUser,
      customTokenVerified,
      patientConsent,
      patientData,
      registered,
    },
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isEmailVerified = currentUser?.emailVerified;
  const isLoggedIn = logged === userStates.LOGGED_IN;
  const { addLoading, removeLoading } = useLoading();

  const emptyLoggedPatientData =
    registered && !patientData && logged === userStates.LOGGED_IN;

  const redirectUser = useCallback(
    (route, conditions) => {
      if (location.pathname !== route && conditions) {
        return navigate(route);
      }
    },
    [navigate, location.pathname]
  );

  useEffect(() => {
    if (
      (patientData && patientConsent === maybeUnknownState.UNKNOWN) ||
      emptyLoggedPatientData
    ) {
      addLoading();
    } else {
      removeLoading();
    }
  }, [
    patientData,
    patientConsent,
    addLoading,
    removeLoading,
    emptyLoggedPatientData,
  ]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const isUserSkippedVerificationStep =
      location.pathname !== "/user/birthdate" &&
      location.pathname !== "/user/birthdate/error" &&
      isCustomTokenLogin &&
      customTokenVerified === "false";

    if (isUserSkippedVerificationStep) {
      logout();
      navigate("/user/login", {
        state: { message: "Falha ao validar conta" },
      });
    }

    const routeGuards = getRouteGuards(isCustomTokenLogin, userState);
    routeGuards.forEach(({ route, conditions }) =>
      redirectUser(route, conditions)
    );
  }, [
    customTokenVerified,
    isCustomTokenLogin,
    isEmailVerified,
    location.pathname,
    patientConsent,
    registered,
    isLoggedIn,
    logout,
    navigate,
    redirectUser,
    userState,
  ]);

  return isLoggedIn ? (
    <Outlet />
  ) : (
    <Navigate to="/user/login" state={{ from: location }} />
  );
};
